import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  getSupervisorReviewDashboard, 
  approveDailyReport, 
  requestChanges, 
  escalateDailyReport,
  addInternalNote,
  reassignWorkOrder,
  sendProgressReminders,
  getSupervisorDailyReport
} from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertTriangle,
  Shield,
  Briefcase,
  Calendar,
  User,
  MapPin,
  FileText,
  MessageSquare,
  Send,
  RefreshCw,
  Filter,
  Search,
  ChevronRight,
  Eye,
  Edit,
  UserMinus,
  Flag,
  Bell,
  ClipboardCheck,
  TrendingUp,
  AlertCircle,
  X,
  Image,
  Loader2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

// Status badge component
const ReviewStatusBadge = ({ status }) => {
  const statusConfig = {
    not_reviewed: { label: 'Not Reviewed', className: 'bg-slate-500' },
    approved: { label: 'Approved', className: 'bg-green-600' },
    changes_requested: { label: 'Changes Requested', className: 'bg-amber-600' },
    escalated: { label: 'Escalated', className: 'bg-red-600' }
  };
  
  const config = statusConfig[status] || statusConfig.not_reviewed;
  return <Badge className={`${config.className} text-white`}>{config.label}</Badge>;
};

// Submission status badge
const SubmissionStatusBadge = ({ status }) => {
  if (status === 'on_time') {
    return <Badge className="bg-green-600 text-white">On Time</Badge>;
  }
  return <Badge className="bg-amber-600 text-white">Late</Badge>;
};

// Expandable text component
const ExpandableText = ({ text, maxLines = 3 }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!text) return <span className="text-slate-400 italic">No details provided</span>;
  
  const lines = text.split('\n');
  const needsTruncation = lines.length > maxLines || text.length > 200;
  
  return (
    <div className="break-words whitespace-pre-wrap overflow-wrap-anywhere text-white">
      <p className={!expanded && needsTruncation ? 'line-clamp-3' : ''}>
        {text}
      </p>
      {needsTruncation && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-blue-400 hover:text-blue-300 text-sm mt-2 flex items-center gap-1 font-medium"
        >
          {expanded ? (
            <>View Less <ChevronUp className="w-3 h-3" /></>
          ) : (
            <>View More... <ChevronDown className="w-3 h-3" /></>
          )}
        </button>
      )}
    </div>
  );
};

const SupervisorReviewPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('todays_reports');
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDetails, setReportDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  // Action dialogs
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showChangesDialog, setShowChangesDialog] = useState(false);
  const [showEscalateDialog, setShowEscalateDialog] = useState(false);
  const [showInternalNoteDialog, setShowInternalNoteDialog] = useState(false);
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  
  // Form states
  const [approveComment, setApproveComment] = useState('');
  const [changesMessage, setChangesMessage] = useState('');
  const [escalationType, setEscalationType] = useState('safety');
  const [escalationDetails, setEscalationDetails] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [newCrewLeader, setNewCrewLeader] = useState('');
  const [reassignReason, setReassignReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, [filterType]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterType !== 'all') params.filter_type = filterType;
      const response = await getSupervisorReviewDashboard(params);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load supervisor dashboard');
    } finally {
      setLoading(false);
    }
  };

  const openReportDetails = async (report) => {
    setSelectedReport(report);
    setDetailsLoading(true);
    try {
      const response = await getSupervisorDailyReport(report.id);
      setReportDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch report details:', error);
      toast.error('Failed to load report details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedReport) return;
    setActionLoading(true);
    try {
      await approveDailyReport(selectedReport.id, approveComment || null);
      toast.success('Report approved successfully');
      setShowApproveDialog(false);
      setApproveComment('');
      setSelectedReport(null);
      setReportDetails(null);
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to approve report');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestChanges = async () => {
    if (!selectedReport || !changesMessage.trim()) {
      toast.error('Please provide a message for the crew leader');
      return;
    }
    setActionLoading(true);
    try {
      await requestChanges(selectedReport.id, changesMessage);
      toast.success('Changes requested - Crew leader has been notified');
      setShowChangesDialog(false);
      setChangesMessage('');
      setSelectedReport(null);
      setReportDetails(null);
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to request changes');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEscalate = async () => {
    if (!selectedReport || !escalationDetails.trim()) {
      toast.error('Please provide escalation details');
      return;
    }
    setActionLoading(true);
    try {
      await escalateDailyReport(selectedReport.id, escalationType, escalationDetails);
      toast.success(`Report escalated (${escalationType.toUpperCase()}) - Admins have been notified`);
      setShowEscalateDialog(false);
      setEscalationType('safety');
      setEscalationDetails('');
      setSelectedReport(null);
      setReportDetails(null);
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to escalate report');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddInternalNote = async () => {
    if (!selectedReport || !internalNote.trim()) {
      toast.error('Please enter a note');
      return;
    }
    setActionLoading(true);
    try {
      await addInternalNote(selectedReport.id, internalNote);
      toast.success('Internal note added');
      setShowInternalNoteDialog(false);
      setInternalNote('');
      // Refresh report details
      const response = await getSupervisorDailyReport(selectedReport.id);
      setReportDetails(response.data);
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReassign = async () => {
    if (!reportDetails || !newCrewLeader) {
      toast.error('Please select a new crew leader');
      return;
    }
    setActionLoading(true);
    try {
      await reassignWorkOrder(reportDetails.work_order.id, newCrewLeader, reassignReason || null);
      toast.success('Work order reassigned successfully');
      setShowReassignDialog(false);
      setNewCrewLeader('');
      setReassignReason('');
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to reassign work order');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendReminders = async () => {
    try {
      const response = await sendProgressReminders();
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to send reminders');
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    try {
      return new Date(isoString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return '-';
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    try {
      return new Date(isoString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  // Filter reports based on search
  const filteredReports = (data?.todays_reports || []).filter(report => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      report.wo_number?.toLowerCase().includes(query) ||
      report.location_name?.toLowerCase().includes(query) ||
      report.crew_leader_name?.toLowerCase().includes(query)
    );
  });

  const filteredMissing = (data?.missing_reports || []).filter(report => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      report.wo_number?.toLowerCase().includes(query) ||
      report.location_name?.toLowerCase().includes(query) ||
      report.crew_leader_name?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Supervisor Review Mode
          </h1>
          <p className="text-slate-600">Review, approve, and manage daily progress reports</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSendReminders}>
            <Bell className="w-4 h-4 mr-2" />
            Send Reminders
          </Button>
          <Button onClick={fetchDashboard}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <Card className="bg-emerald-950/30 border-emerald-800/40">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-slate-700">{data?.summary?.total_reports_today || 0}</p>
            <p className="text-xs text-slate-500">Total Reports</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-amber-600">{data?.summary?.needs_review || 0}</p>
            <p className="text-xs text-amber-700">Needs Review</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-green-600">{data?.summary?.approved || 0}</p>
            <p className="text-xs text-green-700">Approved</p>
          </CardContent>
        </Card>
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-orange-600">{data?.summary?.changes_requested || 0}</p>
            <p className="text-xs text-orange-700">Changes Req</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-red-600">{data?.summary?.escalated || 0}</p>
            <p className="text-xs text-red-700">Escalated</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-purple-600">{data?.summary?.has_flags || 0}</p>
            <p className="text-xs text-purple-700">Has Flags</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-yellow-600">{data?.summary?.late_submissions || 0}</p>
            <p className="text-xs text-yellow-700">Late</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-gray-600">{data?.summary?.missing_reports || 0}</p>
            <p className="text-xs text-gray-700">Missing</p>
          </CardContent>
        </Card>
      </div>

      {/* Review SLA Stats */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              <span className="font-medium">Review Target: {data?.review_deadline_hour || 15}:00</span>
              {data?.is_past_review_deadline && (
                <Badge className="bg-amber-500">Past Deadline</Badge>
              )}
            </div>
            <div className="flex gap-6 text-sm">
              <span className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-green-700">{data?.summary?.reviewed_on_time || 0} On-Time</span>
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4 text-amber-600" />
                <span className="text-amber-700">{data?.summary?.reviewed_late || 0} Late</span>
              </span>
              <span className="flex items-center gap-1">
                <AlertCircle className="w-4 h-4 text-slate-500" />
                <span className="text-slate-600">{data?.summary?.awaiting_review || 0} Awaiting</span>
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by WO#, location, or crew leader..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[180px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Reports</SelectItem>
            <SelectItem value="needs_review">🟡 Needs Review</SelectItem>
            <SelectItem value="has_flags">🚩 Has Safety/Union Flags</SelectItem>
            <SelectItem value="late">⏰ Late Submissions</SelectItem>
            <SelectItem value="escalated">🔴 Escalated</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3 max-w-lg">
          <TabsTrigger value="todays_reports" className="flex items-center gap-1">
            <FileText className="w-4 h-4" />
            Today&apos;s Reports
            {data?.summary?.needs_review > 0 && (
              <Badge className="bg-amber-500 text-white ml-1">{data.summary.needs_review}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="missing" className="flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" />
            Missing
            {data?.summary?.missing_reports > 0 && (
              <Badge className="bg-red-500 text-white ml-1">{data.summary.missing_reports}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="escalated" className="flex items-center gap-1">
            <Flag className="w-4 h-4" />
            Escalated
            {data?.escalated_jobs?.length > 0 && (
              <Badge className="bg-red-600 text-white ml-1">{data.escalated_jobs.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Today's Reports Tab */}
        <TabsContent value="todays_reports">
          <Card className="bg-emerald-950/20 border-emerald-800/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-emerald-100">
                <FileText className="w-5 h-5 text-emerald-400" />
                Today&apos;s Daily Reports ({filteredReports.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredReports.length === 0 ? (
                <div className="text-center py-12 text-emerald-400/70">
                  <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No reports match your filters</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-emerald-800/30 bg-emerald-950/50">
                        <th className="text-left p-3 font-medium text-emerald-300">WO #</th>
                        <th className="text-left p-3 font-medium text-emerald-300">Location</th>
                        <th className="text-left p-3 font-medium text-emerald-300">Crew Leader</th>
                        <th className="text-left p-3 font-medium text-emerald-300">Time</th>
                        <th className="text-center p-3 font-medium text-emerald-300">%</th>
                        <th className="text-center p-3 font-medium text-emerald-300">Status</th>
                        <th className="text-center p-3 font-medium text-emerald-300">Flags</th>
                        <th className="text-center p-3 font-medium text-emerald-300">Review</th>
                        <th className="text-center p-3 font-medium text-emerald-300">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredReports.map((report) => (
                        <tr key={report.id} className="border-b border-emerald-800/20 hover:bg-emerald-900/30">
                          <td className="p-3">
                            <Link to={`/work-orders/${report.work_order_id}`} className="text-blue-600 hover:underline font-mono">
                              {report.wo_number}
                            </Link>
                          </td>
                          <td className="p-3 max-w-[150px] truncate">{report.location_name}</td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-slate-400" />
                              {report.crew_leader_name}
                            </div>
                          </td>
                          <td className="p-3">{formatTime(report.submitted_at)}</td>
                          <td className="p-3 text-center font-bold text-blue-600">{report.percent_complete}%</td>
                          <td className="p-3 text-center">
                            <SubmissionStatusBadge status={report.status} />
                          </td>
                          <td className="p-3 text-center">
                            <div className="flex justify-center gap-1">
                              {report.safety_flag && (
                                <Shield className="w-4 h-4 text-red-500" title="Safety Flag" />
                              )}
                              {report.union_flag && (
                                <Briefcase className="w-4 h-4 text-amber-500" title="Union Flag" />
                              )}
                              {!report.safety_flag && !report.union_flag && (
                                <span className="text-slate-400">—</span>
                              )}
                            </div>
                          </td>
                          <td className="p-3 text-center">
                            <ReviewStatusBadge status={report.review_status} />
                          </td>
                          <td className="p-3 text-center">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openReportDetails(report)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Review
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Missing Reports Tab */}
        <TabsContent value="missing">
          <Card className="bg-emerald-950/20 border-emerald-800/30">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2 text-emerald-100">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Missing Reports ({filteredMissing.length})
                </CardTitle>
                <Button variant="outline" size="sm" onClick={handleSendReminders}>
                  <Bell className="w-4 h-4 mr-2" />
                  Send All Reminders
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {filteredMissing.length === 0 ? (
                <div className="text-center py-12 text-green-400">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-70" />
                  <p className="font-medium">All active work orders have submitted reports today!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-emerald-800/30 bg-emerald-950/50">
                        <th className="text-left p-3 font-medium text-emerald-300">WO #</th>
                        <th className="text-left p-3 font-medium text-emerald-300">Location</th>
                        <th className="text-left p-3 font-medium text-emerald-300">Crew Leader</th>
                        <th className="text-center p-3 font-medium text-emerald-300">Days Since Update</th>
                        <th className="text-center p-3 font-medium text-emerald-300">Last %</th>
                        <th className="text-center p-3 font-medium text-emerald-300">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMissing.map((wo) => (
                        <tr key={wo.work_order_id} className="border-b border-emerald-800/20 hover:bg-emerald-900/30">
                          <td className="p-3">
                            <Link to={`/work-orders/${wo.work_order_id}`} className="text-blue-400 hover:underline font-mono">
                              {wo.wo_number}
                            </Link>
                          </td>
                          <td className="p-3 max-w-[150px] truncate text-emerald-100">{wo.location_name}</td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-slate-400" />
                              {wo.crew_leader_name}
                            </div>
                          </td>
                          <td className="p-3 text-center">
                            <Badge className={wo.days_since_last_update > 2 ? 'bg-red-500' : 'bg-amber-500'}>
                              {wo.days_since_last_update} days
                            </Badge>
                          </td>
                          <td className="p-3 text-center font-bold text-slate-600">
                            {wo.last_percent_complete}%
                          </td>
                          <td className="p-3 text-center">
                            <Link to={`/work-orders/${wo.work_order_id}`}>
                              <Button size="sm" variant="outline">
                                <ChevronRight className="w-4 h-4 mr-1" />
                                Open WO
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Escalated Tab */}
        <TabsContent value="escalated">
          <Card className="bg-emerald-950/20 border-emerald-800/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-emerald-100">
                <Flag className="w-5 h-5 text-red-500" />
                Escalated Jobs ({data?.escalated_jobs?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(!data?.escalated_jobs || data.escalated_jobs.length === 0) ? (
                <div className="text-center py-12 text-emerald-400/70">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No escalated jobs</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {data.escalated_jobs.map((report) => (
                    <Card key={report.id} className="border-red-800/50 bg-red-950/30">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Link to={`/work-orders/${report.work_order_id}`} className="text-blue-400 hover:underline font-mono font-bold">
                                {report.wo_number}
                              </Link>
                              <Badge className={`
                                ${report.escalation_type === 'safety' ? 'bg-red-600' : ''}
                                ${report.escalation_type === 'union' ? 'bg-amber-600' : ''}
                                ${report.escalation_type === 'schedule' ? 'bg-purple-600' : ''}
                              `}>
                                {report.escalation_type?.toUpperCase()}
                              </Badge>
                            </div>
                            <p className="text-sm text-emerald-200 mb-2">{report.location_name}</p>
                            <div className="text-sm">
                              <span className="text-emerald-400/70">Escalated by: </span>
                              <span className="font-medium text-emerald-100">{report.review_user_name}</span>
                              <span className="text-emerald-400/50 ml-2">{formatDate(report.review_timestamp)}</span>
                            </div>
                            <p className="mt-2 text-sm text-emerald-200 break-words whitespace-pre-wrap">
                              {report.escalation_notes}
                            </p>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => openReportDetails(report)}>
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Report Details Panel */}
      <Dialog open={!!selectedReport} onOpenChange={(open) => !open && (setSelectedReport(null), setReportDetails(null))}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <FileText className="w-5 h-5 text-blue-400" />
              Review Daily Progress Report
            </DialogTitle>
          </DialogHeader>
          
          {detailsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : reportDetails ? (
            <ScrollArea className="h-[calc(90vh-200px)]">
              <div className="space-y-6 pr-4">
                {/* Work Order Header */}
                <Card className="bg-slate-800 border-slate-600">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wide">WO #</p>
                        <p className="font-mono font-bold text-blue-400">{reportDetails.work_order?.wo_number}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wide">Location</p>
                        <p className="text-sm text-white truncate">{reportDetails.work_order?.location_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wide">Crew Leader</p>
                        <p className="text-sm text-white flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {reportDetails.crew_leader?.full_name || 'Unassigned'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wide">Overall Progress</p>
                        <p className="text-lg font-bold text-green-400">{reportDetails.work_order?.percent_complete || 0}%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Report Details */}
                <Card className="bg-slate-800 border-slate-600">
                  <CardHeader className="pb-3 bg-slate-700/50">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base text-white font-semibold">Report Details</CardTitle>
                      <div className="flex items-center gap-2">
                        <SubmissionStatusBadge status={reportDetails.report?.status} />
                        <ReviewStatusBadge status={reportDetails.report?.review_status} />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4 pt-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wide">Date</p>
                        <p className="text-white font-medium">{formatDate(reportDetails.report?.submitted_at)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wide">Time</p>
                        <p className="text-white font-medium">{formatTime(reportDetails.report?.submitted_at)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wide">% Complete</p>
                        <p className="font-bold text-green-400 text-lg">{reportDetails.report?.percent_complete}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wide">Hours Worked</p>
                        <p className="text-white font-medium">{reportDetails.report?.hours_worked}h</p>
                      </div>
                    </div>

                    <div>
                      <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">Work Completed</p>
                      <div className="bg-slate-900 border border-slate-600 rounded-lg p-4 text-sm">
                        <ExpandableText text={reportDetails.report?.work_completed_text} />
                      </div>
                    </div>

                    {reportDetails.report?.delays_issues_text && (
                      <div>
                        <p className="text-xs text-amber-400 uppercase tracking-wide mb-2">Delays / Issues</p>
                        <div className="bg-amber-950/50 border border-amber-700 rounded-lg p-4 text-sm">
                          <ExpandableText text={reportDetails.report.delays_issues_text} />
                        </div>
                      </div>
                    )}

                    {reportDetails.report?.material_needed_text && (
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">Material Needed</p>
                        <div className="bg-slate-900 border border-slate-600 rounded-lg p-4 text-sm">
                          <ExpandableText text={reportDetails.report.material_needed_text} />
                        </div>
                      </div>
                    )}

                    {/* Flags */}
                    <div className="flex flex-wrap gap-3">
                      {reportDetails.report?.weather_impact && (
                        <div className="bg-blue-950/50 border border-blue-600 rounded-lg p-3 flex-1 min-w-[200px]">
                          <p className="text-xs text-blue-300 mb-1 flex items-center gap-1 font-medium">
                            <AlertTriangle className="w-3 h-3" />
                            Weather Impact
                          </p>
                          <p className="text-sm text-white">{reportDetails.report.weather_impact_notes || 'Yes'}</p>
                        </div>
                      )}
                      {reportDetails.report?.safety_flag && (
                        <div className="bg-red-950/50 border border-red-600 rounded-lg p-3 flex-1 min-w-[200px]">
                          <p className="text-xs text-red-300 mb-1 flex items-center gap-1 font-medium">
                            <Shield className="w-3 h-3" />
                            Safety Flag
                          </p>
                          <p className="text-sm text-white">{reportDetails.report.safety_details || 'Flagged'}</p>
                        </div>
                      )}
                      {reportDetails.report?.union_flag && (
                        <div className="bg-amber-950/50 border border-amber-600 rounded-lg p-3 flex-1 min-w-[200px]">
                          <p className="text-xs text-amber-300 mb-1 flex items-center gap-1 font-medium">
                            <Briefcase className="w-3 h-3" />
                            Union Flag
                          </p>
                          <p className="text-sm text-white">{reportDetails.report.union_details || 'Flagged'}</p>
                        </div>
                      )}
                    </div>

                    {/* Photos */}
                    {reportDetails.photos?.length > 0 && (
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">Photos ({reportDetails.photos.length})</p>
                        <div className="flex flex-wrap gap-2">
                          {reportDetails.photos.map((photo, index) => (
                            <a 
                              key={index} 
                              href={photo.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="block"
                            >
                              <div className="w-20 h-20 bg-slate-700 rounded-lg overflow-hidden hover:ring-2 ring-blue-500 transition-all">
                                <img 
                                  src={photo.thumbnail_url || photo.url} 
                                  alt={photo.filename || `Photo ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* AskStewie Review */}
                    {reportDetails.report?.safety_review && (
                      <div className="bg-purple-950/50 border border-purple-600 rounded-lg p-4">
                        <p className="text-xs text-purple-300 mb-2 flex items-center gap-1 font-medium">
                          <MessageSquare className="w-3 h-3" />
                          AskStewie Safety/Union Review
                        </p>
                        <div className="text-sm text-white">
                          <ExpandableText text={reportDetails.report.safety_review.review_text} />
                        </div>
                      </div>
                    )}

                    {/* Supervisor Comment (if any) */}
                    {reportDetails.report?.supervisor_comment && (
                      <div className="bg-blue-950/50 border border-blue-600 rounded-lg p-4">
                        <p className="text-xs text-blue-300 mb-2 font-medium">Supervisor Comment</p>
                        <p className="text-sm text-white">{reportDetails.report.supervisor_comment}</p>
                        {reportDetails.reviewer && (
                          <p className="text-xs text-slate-400 mt-2">
                            — {reportDetails.reviewer.full_name}, {formatDate(reportDetails.report.review_timestamp)}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Internal Notes (supervisor only) */}
                    {reportDetails.report?.supervisor_internal_notes?.length > 0 && (
                      <div className="bg-slate-900 border border-slate-600 rounded-lg p-4">
                        <p className="text-xs text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          Internal Notes (Supervisors Only)
                        </p>
                        <div className="space-y-2">
                          {reportDetails.report.supervisor_internal_notes.map((note, index) => (
                            <div key={index} className="text-sm border-l-2 border-slate-500 pl-3">
                              <p className="break-words whitespace-pre-wrap text-white">{note.note}</p>
                              <p className="text-xs text-slate-400 mt-1">
                                — {note.added_by_name}, {formatDate(note.added_at)}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 sticky bottom-0 bg-slate-900 py-3 border-t border-slate-700">
                  <Button 
                    onClick={() => setShowApproveDialog(true)}
                    className="bg-green-600 hover:bg-green-700"
                    disabled={reportDetails.report?.review_status === 'approved'}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                  <Button 
                    onClick={() => setShowChangesDialog(true)}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Request Changes
                  </Button>
                  <Button 
                    onClick={() => setShowEscalateDialog(true)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Flag className="w-4 h-4 mr-2" />
                    Escalate
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setShowInternalNoteDialog(true)}
                    className="border-emerald-700/50 text-emerald-300 hover:bg-emerald-900/30"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Add Note
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setShowReassignDialog(true)}
                    className="border-emerald-700/50 text-emerald-300 hover:bg-emerald-900/30"
                  >
                    <UserMinus className="w-4 h-4 mr-2" />
                    Reassign
                  </Button>
                </div>
              </div>
            </ScrollArea>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent className="bg-emerald-950 border-emerald-800/50 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-400">
              <CheckCircle className="w-5 h-5" />
              Approve Report
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Mark this report as reviewed and approved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Comment (Optional)</Label>
              <Textarea
                value={approveComment}
                onChange={(e) => setApproveComment(e.target.value)}
                placeholder="Add an optional comment for the crew leader..."
                className="mt-2 bg-emerald-900/50 border-emerald-700/50 text-white placeholder:text-emerald-400/50"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)} className="border-emerald-700/50 text-emerald-300">
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={actionLoading} className="bg-green-600 hover:bg-green-700">
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Approve Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Changes Dialog */}
      <Dialog open={showChangesDialog} onOpenChange={setShowChangesDialog}>
        <DialogContent className="bg-emerald-950 border-emerald-800/50 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <Edit className="w-5 h-5" />
              Request Changes
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Send a message to the crew leader requesting clarification or changes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Message to Crew Leader *</Label>
              <Textarea
                value={changesMessage}
                onChange={(e) => setChangesMessage(e.target.value)}
                placeholder="Please clarify which bay you completed and what is left for tomorrow..."
                className="mt-2 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 min-h-[100px]"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangesDialog(false)} className="border-emerald-700/50 text-emerald-300">
              Cancel
            </Button>
            <Button onClick={handleRequestChanges} disabled={actionLoading || !changesMessage.trim()} className="bg-amber-600 hover:bg-amber-700">
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Escalate Dialog */}
      <Dialog open={showEscalateDialog} onOpenChange={setShowEscalateDialog}>
        <DialogContent className="bg-emerald-950 border-emerald-800/50 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Flag className="w-5 h-5" />
              Escalate Report
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Flag and escalate this report. Admins will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Escalation Type *</Label>
              <Select value={escalationType} onValueChange={setEscalationType}>
                <SelectTrigger className="mt-2 bg-emerald-900/50 border-emerald-700/50 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="safety">🛡️ Safety Concern</SelectItem>
                  <SelectItem value="union">⚖️ Union / Overtime Concern</SelectItem>
                  <SelectItem value="schedule">📅 Schedule / Performance Concern</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Details *</Label>
              <Textarea
                value={escalationDetails}
                onChange={(e) => setEscalationDetails(e.target.value)}
                placeholder="Describe the concern and why this needs escalation..."
                className="mt-2 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 min-h-[100px]"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEscalateDialog(false)} className="border-emerald-700/50 text-emerald-300">
              Cancel
            </Button>
            <Button onClick={handleEscalate} disabled={actionLoading || !escalationDetails.trim()} className="bg-red-600 hover:bg-red-700">
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
              Escalate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Internal Note Dialog */}
      <Dialog open={showInternalNoteDialog} onOpenChange={setShowInternalNoteDialog}>
        <DialogContent className="bg-emerald-950 border-emerald-800/50 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Add Internal Note
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This note is only visible to supervisors and admins.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Note *</Label>
              <Textarea
                value={internalNote}
                onChange={(e) => setInternalNote(e.target.value)}
                placeholder="Add private notes about this report..."
                className="mt-2 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 min-h-[100px]"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInternalNoteDialog(false)} className="border-emerald-700/50 text-emerald-300">
              Cancel
            </Button>
            <Button onClick={handleAddInternalNote} disabled={actionLoading || !internalNote.trim()} className="bg-blue-600 hover:bg-blue-700">
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
              Add Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Dialog */}
      <Dialog open={showReassignDialog} onOpenChange={setShowReassignDialog}>
        <DialogContent className="bg-emerald-950 border-emerald-800/50 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserMinus className="w-5 h-5" />
              Reassign Work Order
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Assign this work order to a different crew leader. The new crew leader will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">New Crew Leader *</Label>
              <Select value={newCrewLeader} onValueChange={setNewCrewLeader}>
                <SelectTrigger className="mt-2 bg-emerald-900/50 border-emerald-700/50 text-white">
                  <SelectValue placeholder="Select a crew leader..." />
                </SelectTrigger>
                <SelectContent>
                  {reportDetails?.available_crew_leaders?.filter(cl => cl.id !== reportDetails?.crew_leader?.id).map((cl) => (
                    <SelectItem key={cl.id} value={cl.id}>
                      {cl.full_name} ({cl.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Reason (Optional)</Label>
              <Textarea
                value={reassignReason}
                onChange={(e) => setReassignReason(e.target.value)}
                placeholder="Provide a reason for the reassignment..."
                className="mt-2 bg-emerald-900/50 border-emerald-700/50 text-white placeholder:text-emerald-400/50"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReassignDialog(false)} className="border-emerald-700/50 text-emerald-300">
              Cancel
            </Button>
            <Button onClick={handleReassign} disabled={actionLoading || !newCrewLeader} className="bg-blue-600 hover:bg-blue-700">
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserMinus className="w-4 h-4 mr-2" />}
              Reassign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupervisorReviewPage;
